import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useProfile, QUERY_KEYS } from "@/hooks/use-queries";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Save, Loader2, User as UserIcon, Mail, Phone, MapPin,
  Heart, HeartPulse, HeartHandshake, Bell, Shield, Calendar, HandHeart,
  Activity, Brain, Baby, Ribbon, Stethoscope, Pill,
  Wind, Accessibility, Bone, Smile, Siren
} from "lucide-react";

const CONDITION_LABELS: Record<string, { label: string; icon: typeof Heart }> = {
  "congenital-heart-disease": { label: "Congenital Heart Disease", icon: HeartPulse },
  "pediatric-cancer": { label: "Pediatric Cancer & Blood Disorders", icon: Ribbon },
  "nicu-neonatal": { label: "NICU & Neonatal Care", icon: Baby },
  "epilepsy-neurology": { label: "Epilepsy & Neurological Conditions", icon: Brain },
  "asthma-respiratory": { label: "Asthma & Respiratory Conditions", icon: Wind },
  "type-1-diabetes": { label: "Type 1 Diabetes & Endocrine Disorders", icon: Activity },
  "pediatric-mental-health": { label: "Pediatric Mental Health", icon: Brain },
  "cerebral-palsy": { label: "Cerebral Palsy & Developmental Disabilities", icon: Accessibility },
  "kidney-disease": { label: "Kidney Disease & Nephrology", icon: Stethoscope },
  "orthopedic-spinal": { label: "Orthopedic & Spinal Conditions", icon: Bone },
  "cleft-craniofacial": { label: "Cleft & Craniofacial Conditions", icon: Smile },
  "fetal-prenatal": { label: "Fetal & Prenatal Conditions", icon: HeartHandshake },
  "trauma-emergency": { label: "Pediatric Trauma & Emergency Care", icon: Siren },
};

export default function ProfilePage() {
  usePageTitle("My Profile", "Manage your HealthWallet profile, account settings, and notification preferences.");
  const { user } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const { data: profileData, isLoading } = useProfile();
  const profile = profileData ?? user;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    bio: "",
    phone: "",
    zipCode: "",
    supportInterests: [] as string[],
    notifications: {
      fundUpdates: true,
      communityNews: true,
      impactReports: true,
      newConditions: false,
    },
  });

  useEffect(() => {
    if (profile) {
      let notifs = { fundUpdates: true, communityNews: true, impactReports: true, newConditions: false };
      try {
        if (profile.notificationPreferences) {
          notifs = { ...notifs, ...JSON.parse(profile.notificationPreferences) };
        }
      } catch {}
      setFormData({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        bio: profile.bio || "",
        phone: profile.phone || "",
        zipCode: profile.zipCode || "",
        supportInterests: profile.supportInterests || [],
        notifications: notifs,
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("PATCH", "/api/profile", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profile });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setIsEditing(false);
      toast({
        title: "Profile updated",
        description: "Your changes have been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      bio: formData.bio,
      phone: formData.phone,
      zipCode: formData.zipCode,
      supportInterests: formData.supportInterests,
      notificationPreferences: JSON.stringify(formData.notifications),
    });
  };

  const toggleInterest = (slug: string) => {
    setFormData(prev => ({
      ...prev,
      supportInterests: prev.supportInterests.includes(slug)
        ? prev.supportInterests.filter(s => s !== slug)
        : [...prev.supportInterests, slug],
    }));
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!profile) return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 text-center text-muted-foreground">
      Unable to load profile. Please refresh the page.
    </div>
  );

  const initials = `${(profile.firstName || "")[0] || ""}${(profile.lastName || "")[0] || ""}`.toUpperCase();
  const memberSince = profile.createdAt ? new Date(profile.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">My Profile</h1>
          <p className="text-muted-foreground text-sm">Manage your supporter information</p>
        </div>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} data-testid="button-edit-profile">
            Edit profile
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => {
              setIsEditing(false);
              if (profile) {
                let notifs = { fundUpdates: true, communityNews: true, impactReports: true, newConditions: false };
                try {
                  if (profile.notificationPreferences) {
                    notifs = { ...notifs, ...JSON.parse(profile.notificationPreferences) };
                  }
                } catch {}
                setFormData({
                  firstName: profile.firstName || "",
                  lastName: profile.lastName || "",
                  email: profile.email || "",
                  bio: profile.bio || "",
                  phone: profile.phone || "",
                  zipCode: profile.zipCode || "",
                  supportInterests: profile.supportInterests || [],
                  notifications: notifs,
                });
              }
            }} data-testid="button-cancel-edit">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-profile">
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={profile.profileImageUrl || undefined} />
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName" className="text-xs">First name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      data-testid="input-profile-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-xs">Last name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      data-testid="input-profile-lastname"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <CardTitle className="text-xl" data-testid="text-profile-name">
                    {profile.firstName} {profile.lastName}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">
                      <HandHeart className="w-3 h-3 mr-1" />
                      Community Supporter
                    </Badge>
                    {memberSince && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Member since {memberSince}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" /> Email
              </Label>
              {isEditing ? (
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1"
                  data-testid="input-profile-email"
                />
              ) : (
                <p className="text-sm font-medium mt-1" data-testid="text-profile-email">{profile.email}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <UserIcon className="w-3 h-3" /> Username
              </Label>
              <p className="text-sm font-medium mt-1" data-testid="text-profile-username">@{profile.username}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone
              </Label>
              {isEditing ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(612) 555-0100"
                  className="mt-1"
                  data-testid="input-profile-phone"
                />
              ) : (
                <p className="text-sm font-medium mt-1" data-testid="text-profile-phone">
                  {profile.phone || "Not set"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Zip code
              </Label>
              {isEditing ? (
                <Input
                  value={formData.zipCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                  placeholder="55401"
                  className="mt-1"
                  maxLength={5}
                  data-testid="input-profile-zipcode"
                />
              ) : (
                <p className="text-sm font-medium mt-1" data-testid="text-profile-zipcode">
                  {profile.zipCode || "Not set"}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">About</Label>
            {isEditing ? (
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell the community about yourself..."
                className="mt-1 resize-none"
                rows={3}
                maxLength={500}
                data-testid="input-profile-bio"
              />
            ) : (
              <p className="text-sm mt-1" data-testid="text-profile-bio">
                {profile.bio || "No bio yet"}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="w-4 h-4 text-primary" />
            Support interests
          </CardTitle>
          <CardDescription>
            {isEditing ? "Select or deselect health conditions" : "Health conditions you care about"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CONDITION_LABELS).map(([slug, { label, icon: Icon }]) => {
                const isSelected = formData.supportInterests.includes(slug);
                return (
                  <button
                    key={slug}
                    onClick={() => toggleInterest(slug)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "border-muted hover-elevate"
                    }`}
                    data-testid={`interest-edit-${slug}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="profile-interests-display">
              {(profile.supportInterests || []).length > 0 ? (
                (profile.supportInterests || []).map(slug => {
                  const cond = CONDITION_LABELS[slug];
                  const Icon = cond?.icon || Heart;
                  const label = cond?.label || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <Badge key={slug} variant="outline" className="gap-1">
                      <Icon className="w-3 h-3" />
                      {label}
                    </Badge>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No interests selected yet</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            Notification preferences
          </CardTitle>
          <CardDescription>Control what updates you receive</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { key: "fundUpdates" as const, label: "Fund updates", desc: "When funds you support receive contributions" },
              { key: "communityNews" as const, label: "Community news", desc: "Local healthcare partner updates and events" },
              { key: "impactReports" as const, label: "Impact reports", desc: "Monthly reports on your contribution impact" },
              { key: "newConditions" as const, label: "New condition funds", desc: "When new health condition funds launch" },
            ].map((item) => (
              <div
                key={item.key}
                className="flex items-start justify-between gap-3"
                data-testid={`notification-pref-${item.key}`}
              >
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-medium">{item.label}</Label>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <Switch
                  checked={formData.notifications[item.key]}
                  onCheckedChange={(checked) => {
                    if (!isEditing) return;
                    setFormData(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, [item.key]: checked },
                    }));
                  }}
                  disabled={!isEditing}
                  data-testid={`switch-pref-${item.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
